import React from 'react';
import {
  Box,
  Text,
  Image,
  Progress,
  ProgressLabel,
  HStack,
  Stat,
  StatLabel,
  StatNumber,
} from '@chakra-ui/react';

// Helper per generare l'URL del QR code usando l'API di goQR.me
const generateQRCodeUrl = (url, size = 150) => {
  if (!url) return null;
  // Codifica l'URL per l'uso nella query string
  const encodedUrl = encodeURIComponent(url);
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodedUrl}`;
};

const GoalSlide = ({ donationGoal, backgroundImage }) => {
  if (!donationGoal) {
    return (
      <Box
        height="100vh"
        width="100vw"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="black"
      >
        <Text color="white" fontSize="xl">
          Nessun obiettivo di donazione disponibile
        </Text>
      </Box>
    );
  }

  const {
    title: goalTitle,
    description,
    targetAmount = 0,
    currentAmount = 0,
    image: goalImage,
  } = donationGoal;

  // Calcola la percentuale di completamento
  const progressPercentage =
    targetAmount > 0 ? Math.min((currentAmount / targetAmount) * 100, 100) : 0;

  // Colore del progress basato sulla percentuale
  const getProgressColor = (percentage) => {
    if (percentage >= 100) return 'green';
    if (percentage >= 75) return 'blue';
    if (percentage >= 50) return 'yellow';
    if (percentage >= 25) return 'orange';
    return 'red';
  };

  // Formatta l'importo in euro
  const formatAmount = (amount) => {
    return `€${amount.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Estrai l'URL dell'immagine del goal per mostrarla come elemento principale
  let imageUrl = null;
  let backgroundImageUrl = null;

  // Estrai l'immagine del goal per mostrarla come elemento principale
  if (goalImage) {
    // Estrai l'URL dall'oggetto media del goal
    const goalImageUrl =
      typeof goalImage === 'string'
        ? goalImage
        : goalImage.url || (goalImage.data && goalImage.data.url) || null;

    if (goalImageUrl) {
      // Se non è un URL completo, aggiungi l'origine
      imageUrl = goalImageUrl.startsWith('http')
        ? goalImageUrl
        : `${window.location.origin}${goalImageUrl}`;
    }
  }

  // Usa l'immagine di sfondo della gallery come sfondo (non l'immagine del goal)
  if (backgroundImage) {
    backgroundImageUrl =
      typeof backgroundImage === 'string'
        ? backgroundImage
        : backgroundImage.url || null;
  }

  return (
    <Box
      height="100vh"
      width="100vw"
      bg="black"
      position="relative"
      overflow="hidden"
    >
      {/* Immagine di sfondo ruotata (solo se presente, non l'immagine del goal) */}
      {backgroundImageUrl && (
        <Image
          src={backgroundImageUrl}
          alt="Background"
          position="absolute"
          top="50%"
          left="50%"
          width="100vh"
          height="100vw"
          objectFit="cover"
          opacity={1}
          zIndex={0}
          transform="translate(-50%, -50%) rotate(-90deg)"
          transformOrigin="center"
          onError={(e) => {
            console.error('Errore caricamento sfondo GoalSlide:', e);
            console.log('backgroundImageUrl:', backgroundImageUrl);
          }}
        />
      )}

      {/* Contenuto ruotato */}
      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={8}
        transform="rotate(-90deg)"
        transformOrigin="center"
        zIndex={2}
      >
        {/* Container con proporzioni 9:16 per schermo verticale */}
        <Box
          width="90vh"
          height="calc(90vh * 16 / 9)"
          maxWidth="90vw"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          px={4}
        >
          {/* Testo SostieniCIL */}
          {goalTitle && (
            <Box mb={1} textAlign="center" zIndex={3}>
              <Text fontSize="3xl" color="#45505A" fontWeight="bold">
                SostieniCIL con l&apos;acquisto di un
              </Text>
            </Box>
          )}

          {/* Titolo del goal */}
          {goalTitle && (
            <Box mb={0} textAlign="center" zIndex={3}>
              <Text fontSize="7xl" color="#45505A" fontWeight="bold">
                {goalTitle}
              </Text>
            </Box>
          )}

          {/* Descrizione */}
          {description && (
            <Box mb={6} textAlign="center" zIndex={3} maxWidth="80%">
              <Text fontSize="xl" color="#45505A" fontWeight="bold">
                {description}
              </Text>
            </Box>
          )}

          {/* Immagine del goal */}
          {imageUrl && (
            <Box
              mb={6}
              textAlign="center"
              zIndex={3}
              maxWidth="800px"
              width="100%"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Image
                src={imageUrl}
                alt={goalTitle || 'Donation Goal'}
                maxHeight="400px"
                maxWidth="100%"
                objectFit="contain"
                borderRadius="md"
                boxShadow="lg"
                bg="whiteAlpha.100"
                p={2}
                mx="auto"
              />
            </Box>
          )}

          {/* Statistiche importi - sulla stessa riga */}
          <HStack
            spacing={6}
            mb={6}
            width="100%"
            zIndex={3}
            justifyContent="center"
            flexWrap="wrap"
          >
            <Stat
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
              flex="1"
              minWidth="200px"
              maxWidth="400px"
            >
              <StatLabel fontSize="xl" color="#45505A" fontWeight="bold" mb={2}>
                Raccolto
              </StatLabel>
              <StatNumber fontSize="4xl" color="#45505A" fontWeight="extrabold">
                {formatAmount(currentAmount)}
              </StatNumber>
            </Stat>

            <Stat
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
              flex="1"
              minWidth="200px"
              maxWidth="400px"
            >
              <StatLabel fontSize="xl" color="#45505A" fontWeight="bold" mb={2}>
                Obiettivo
              </StatLabel>
              <StatNumber fontSize="4xl" color="#45505A" fontWeight="extrabold">
                {formatAmount(targetAmount)}
              </StatNumber>
            </Stat>
          </HStack>

          {/* Progress bar */}
          <Box width="100%" maxWidth="700px" zIndex={3} mb={4}>
            <Progress
              hasStripe
              height="50px"
              colorScheme={getProgressColor(progressPercentage)}
              value={progressPercentage}
              borderRadius="md"
              size="lg"
            >
              <ProgressLabel fontSize="lg" fontWeight="bold" color="white">
                {progressPercentage.toFixed(1)}%
              </ProgressLabel>
            </Progress>
          </Box>

          {/* Percentuale rimanente */}
          {targetAmount > currentAmount && (
            <Box textAlign="center" zIndex={3}>
              <Text fontSize="xl" color="#45505A" fontWeight="bold">
                Mancano {formatAmount(targetAmount - currentAmount)} per
                raggiungere l&apos;obiettivo!
              </Text>
            </Box>
          )}

          {/* Messaggio di completamento */}
          {progressPercentage >= 100 && (
            <Box textAlign="center" zIndex={3} mt={4}>
              <Text
                fontSize="2xl"
                color="green.300"
                fontWeight="bold"
                textShadow="2px 2px 4px rgba(0,0,0,0.8)"
                bg="blackAlpha.600"
                px={4}
                py={2}
                borderRadius={8}
              >
                🎉 Obiettivo raggiunto!
              </Text>
            </Box>
          )}
        </Box>
      </Box>

      {/* QR Code per la pagina di donazioni */}
      <Box
        position="absolute"
        top="50%"
        right="28px"
        transform="translateY(-50%)"
        bg="white"
        p={2}
        borderRadius="md"
        boxShadow="lg"
        zIndex={20}
      >
        <Image
          src={generateQRCodeUrl(
            'https://www.cilassociazione.it/sostienicil',
            240
          )}
          alt="QR Code"
          width="110px"
          height="110px"
          onError={(e) => {
            console.error('Errore nel caricamento QR code:', e);
            console.log(
              'URL QR code:',
              generateQRCodeUrl(
                'https://www.cilassociazione.it/sostienicil',
                240
              )
            );
          }}
        />
      </Box>
    </Box>
  );
};

export default GoalSlide;
